import React from "react";
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  InputAdornment,
  Stack,
  TextField,
} from "@mui/material";
import { SearchRounded } from "@mui/icons-material";

export type FilterOption = {
  label: string;
  value: string;
};

export type FilterGroupConfig = {
  id: string;
  label: string;
  placeholder?: string;
  options: FilterOption[];
  selectedValues: string[];
  onChange: (values: string[]) => void;
};

export type FilterChipConfig = {
  id: string;
  label: string;
  onDelete: () => void;
};

interface FilterBarProps {
  filterGroups: FilterGroupConfig[];
  searchValue: string;
  onSearchChange: (value: string) => void;
  searchPlaceholder?: string;
  chips?: FilterChipConfig[];
  onClearAll?: () => void;
}

const FilterBar: React.FC<FilterBarProps> = ({
  filterGroups,
  searchValue,
  onSearchChange,
  searchPlaceholder = "Search...",
  chips = [],
  onClearAll,
}) => {
  const hasActiveFilters = chips.length > 0;

  return (
    <Box>
      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={2}
        alignItems={{ xs: "stretch", md: "center" }}
        justifyContent="space-between"
      >
        <Stack direction="row" spacing={1.5} flexWrap="wrap">
          {filterGroups.map((group) => (
            <Autocomplete
              key={group.id}
              multiple
              disableCloseOnSelect
              options={group.options}
              value={group.options.filter((option) =>
                group.selectedValues.includes(option.value),
              )}
              onChange={(_, value) =>
                group.onChange(value.map((option) => option.value))
              }
              getOptionLabel={(option) => option.label}
              renderOption={(props, option, { selected }) => (
                <li {...props}>
                  <Checkbox sx={{ mr: 1 }} size="small" checked={selected} />
                  {option.label}
                </li>
              )}
              sx={{ minWidth: 220 }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  label={group.label}
                  placeholder={group.placeholder ?? "Choose options"}
                  sx={{
                    backgroundColor: "#fff",
                    borderRadius: 1,
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: "#fff",
                      "& fieldset": {
                        border: "none",
                      },
                      "&:hover fieldset": {
                        border: "none",
                      },
                      "&.Mui-focused fieldset": {
                        border: "none",
                      },
                    },
                  }}
                />
              )}
            />
          ))}
        </Stack>
        <TextField
          size="small"
          value={searchValue}
          onChange={(event) => onSearchChange(event.target.value)}
          placeholder={searchPlaceholder}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchRounded fontSize="small" color="action" />
              </InputAdornment>
            ),
          }}
          sx={{
            minWidth: { xs: "100%", md: 280 },
            backgroundColor: "#fff",
            borderRadius: 1,
            "& .MuiOutlinedInput-root": {
              backgroundColor: "#fff",
              "& fieldset": {
                border: "none",
              },
              "&:hover fieldset": {
                border: "none",
              },
              "&.Mui-focused fieldset": {
                border: "none",
              },
            },
          }}
        />
      </Stack>

      {hasActiveFilters && (
        <Box
          mt={1.5}
          display="flex"
          alignItems="center"
          flexWrap="wrap"
          gap={1}
        >
          {chips.map((chip) => (
            <Chip key={chip.id} label={chip.label} onDelete={chip.onDelete} />
          ))}
          {onClearAll && (
            <Button size="small" variant="text" onClick={onClearAll}>
              Clear all
            </Button>
          )}
        </Box>
      )}
    </Box>
  );
};

export default FilterBar;
